import { useEffect, useMemo, useState } from "react";
import Container from "../components/Container";

const EMPTY = { hero: {}, featured: [], gallery: [], albumsSection: {}, albums: [] };

function Lightbox({ items, index, onChange, onClose }) {
  const item = items[index];

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === "Escape") onClose();
      if (event.key === "ArrowLeft") onChange((index + 1) % items.length);
      if (event.key === "ArrowRight") onChange((index - 1 + items.length) % items.length);
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [index, items.length, onChange, onClose]);

  if (!item) return null;

  return (
    <div className="fixed inset-0 z-[70] grid place-items-center bg-[rgba(14,16,13,0.96)] p-3 sm:p-6" role="dialog" aria-modal="true">
      <div className="flex h-full w-full max-w-7xl flex-col">
        <div className="flex h-14 shrink-0 items-center justify-between text-white">
          <div>
            <p className="text-sm font-bold">{item.title}</p>
            <p className="mt-1 text-xs text-white/55">{index + 1} / {items.length}</p>
          </div>
          <button onClick={onClose} className="grid h-10 w-10 place-items-center border border-white/25 text-2xl hover:bg-white hover:text-black" aria-label="إغلاق">×</button>
        </div>
        <div className="relative min-h-0 flex-1">
          <img src={item.image} alt={item.title} className="h-full w-full object-contain" />
          {items.length > 1 && <>
            <button onClick={() => onChange((index - 1 + items.length) % items.length)} className="absolute right-2 top-1/2 grid h-11 w-11 -translate-y-1/2 place-items-center bg-black/55 text-xl text-white hover:bg-white hover:text-black" aria-label="الصورة السابقة">→</button>
            <button onClick={() => onChange((index + 1) % items.length)} className="absolute left-2 top-1/2 grid h-11 w-11 -translate-y-1/2 place-items-center bg-black/55 text-xl text-white hover:bg-white hover:text-black" aria-label="الصورة التالية">←</button>
          </>}
        </div>
      </div>
    </div>
  );
}

function AlbumView({ album, onClose, onOpenImage }) {
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-[#faf9f6]" role="dialog" aria-modal="true">
      <header className="sticky top-0 z-10 border-b border-[#ddd8ce] bg-[rgba(250,249,246,0.96)] backdrop-blur-md">
        <Container>
          <div className="flex min-h-[76px] items-center justify-between gap-5">
            <div className="min-w-0">
              <p className="text-xs font-bold text-[#9a763d]">{album.eyebrow} · {album.items.length} صورة</p>
              <h2 className="mt-1 truncate text-lg font-bold text-[#252923] sm:text-2xl">{album.title}</h2>
            </div>
            <button onClick={onClose} className="grid h-10 w-10 shrink-0 place-items-center border border-[#cbc5ba] bg-white text-2xl" aria-label="إغلاق الألبوم">×</button>
          </div>
        </Container>
      </header>
      <Container>
        <p className="max-w-3xl py-8 text-sm leading-7 text-[#686b65] sm:text-base">{album.description}</p>
        <div className="grid grid-cols-2 gap-2 pb-16 sm:gap-4 md:grid-cols-3 lg:grid-cols-4">
          {album.items.map((item, index) => (
            <button key={item.image} onClick={() => onOpenImage(index)} className="group relative aspect-[4/3] overflow-hidden bg-[#e8e5de]">
              <img src={item.image} alt={item.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
              <span className="absolute bottom-2 left-2 grid h-7 min-w-7 place-items-center bg-black/55 px-2 text-xs text-white">{index + 1}</span>
            </button>
          ))}
        </div>
      </Container>
    </div>
  );
}

function AlbumCard({ album, onOpen }) {
  const previews = album.items.slice(0, 3);
  return (
    <button onClick={onOpen} className="group w-full border-b border-[#dcd7cd] py-7 text-right sm:py-9 lg:border lg:bg-white lg:p-4 lg:pb-7">
      <div className="grid gap-6">
        <div className="grid h-[230px] grid-cols-2 grid-rows-2 gap-1 overflow-hidden bg-[#e8e5de] sm:h-[300px] lg:h-[280px]">
          {previews.map((item, index) => (
            <img key={item.image} src={item.image} alt="" loading="lazy" className={`h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.025] ${index === 0 ? "row-span-2" : ""}`} />
          ))}
        </div>
        <div className="lg:px-3">
          <div className="flex items-center gap-3 text-xs font-bold text-[#9a763d]">
            <span>{album.eyebrow}</span><span className="h-px w-7 bg-[#c4aa7a]" /><span>{album.items.length} صورة</span>
          </div>
          <h3 className="mt-4 text-[25px] font-bold leading-[1.5] text-[#252923] sm:text-[30px] lg:min-h-[90px] lg:text-[26px]">{album.title}</h3>
          <p className="mt-3 line-clamp-2 text-sm leading-7 text-[#686b65]">{album.description}</p>
          <span className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-[#8d6b35]">فتح الألبوم <span aria-hidden="true">←</span></span>
        </div>
      </div>
    </button>
  );
}

export default function Gallery() {
  const [content, setContent] = useState(EMPTY);
  const [category, setCategory] = useState("الكل");
  const [activeAlbum, setActiveAlbum] = useState(null);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    fetch("/data/gallery.json").then((res) => res.json()).then(setContent).catch(console.error);
  }, []);

  const albums = useMemo(() => {
    const churchAlbum = {
      eyebrow: "معالم الكنيسة",
      title: content.galleryTitle || "من داخل الكنيسة وحولها",
      description:
        "مجموعة صور توثّق الكنيسة القديمة والجديدة، وساحتها، وأبرز معالمها الداخلية والخارجية وأيقونة مار الياس.",
      items: [...(content.featured || []), ...(content.gallery || [])],
    };

    const archiveAlbums = (content.albums || []).map((album) => ({
      ...album,
      items: (album.items || []).map((item, index) => ({
        ...item,
        title: item.title || `${album.itemTitlePrefix || album.title} ${index + 1}`,
        description: item.description || album.itemDescription || album.description,
      })),
    }));

    return churchAlbum.items.length ? [churchAlbum, ...archiveAlbums] : archiveAlbums;
  }, [content.albums, content.featured, content.gallery, content.galleryTitle]);

  const categories = useMemo(() => ["الكل", ...new Set(albums.map((album) => album.eyebrow))], [albums]);
  const filteredAlbums = category === "الكل" ? albums : albums.filter((album) => album.eyebrow === category);
  const openAlbumImage = (index) => setLightbox({ items: activeAlbum.items, index });

  return (
    <main>
      <section className="border-b border-[#ddd8ce] bg-[#222720] py-14 text-white sm:py-20">
        <Container>
          <p className="text-sm font-bold text-[#c9aa70]">{content.hero?.eyebrow}</p>
          <h1 className="mt-3 text-[40px] font-extrabold sm:text-[56px]">{content.hero?.title}</h1>
          <p className="mt-5 max-w-2xl text-base leading-8 text-white/70">{content.hero?.description}</p>
          <div className="mt-8 flex gap-8 text-sm">
            <span><strong className="block text-2xl text-white">{albums.length}</strong><span className="text-white/55">ألبوم</span></span>
            <span><strong className="block text-2xl text-white">{albums.reduce((sum, album) => sum + album.items.length, 0)}</strong><span className="text-white/55">صورة أرشيفية</span></span>
          </div>
        </Container>
      </section>

      <section className="bg-[#faf9f6] py-14 sm:py-20">
        <Container>
          <div className="max-w-3xl">
            <p className="text-sm font-bold text-[#9a763d]">{content.albumsSection?.eyebrow}</p>
            <h2 className="mt-2 text-[32px] font-extrabold text-[#252923] sm:text-[40px]">{content.albumsSection?.title}</h2>
            <p className="mt-4 text-sm leading-7 text-[#686b65] sm:text-base">{content.albumsSection?.description}</p>
          </div>

          <div className="-mx-1 mt-8 flex gap-2 overflow-x-auto px-1 pb-3 lg:flex-wrap lg:overflow-visible" aria-label="تصفية الألبومات">
            {categories.map((item) => (
              <button key={item} onClick={() => setCategory(item)} className={`shrink-0 border px-4 py-2 text-sm font-bold transition-colors ${category === item ? "border-[#252923] bg-[#252923] text-white" : "border-[#d2cdc3] bg-white text-[#5d615b] hover:border-[#8d6b35]"}`}>{item}</button>
            ))}
          </div>

          <div className="mt-4 border-t border-[#dcd7cd] lg:grid lg:grid-cols-2 lg:gap-6 lg:border-0">
            {filteredAlbums.map((album) => <AlbumCard key={album.title} album={album} onOpen={() => setActiveAlbum(album)} />)}
          </div>
        </Container>
      </section>

      {activeAlbum && <AlbumView album={activeAlbum} onClose={() => setActiveAlbum(null)} onOpenImage={openAlbumImage} />}
      {lightbox && <Lightbox items={lightbox.items} index={lightbox.index} onChange={(index) => setLightbox((current) => ({ ...current, index }))} onClose={() => setLightbox(null)} />}
    </main>
  );
}
